import { themeBackgrounds, themeColors } from "@dana/MobiusOverrides/styles/colors";
import { fontSizes, fontWeights, lineHeights } from "@dana/MobiusOverrides/styles/typography";
import { cartLineCardCondensedStyles } from "@dana/Overrides/Widgets/Cart/CartLineCardCondensed";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { GridOffset } from "@insite/mobius/GridContainer";
import { IconWrapper } from "@insite/mobius/Icon";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import { css } from "styled-components";

cartLineCardCondensedStyles.container = mergeToNew(cartLineCardCondensedStyles.container, {
    gap: 16,
    css: css`
        border-bottom: 1px solid ${themeBackgrounds.lightGray};
    `,
});

cartLineCardCondensedStyles.productPrice = mergeToNew(cartLineCardCondensedStyles.productPrice, {
    priceLabelText: {
        forwardAs: "span",
        size: fontSizes.small,
        lineHeight: lineHeights.default,
        color: themeBackgrounds.darkGray,
        transform: "capitalize",
        css: css`
            margin-bottom: 10px;
        `,
    },
    price: {
        priceText: {
            size: fontSizes.small,
            lineHeight: lineHeights.default,
        },
        unitOfMeasure: {
            unitOfMeasureText: {
                size: fontSizes.xSmall,
                lineHeight: lineHeights.default,
            },
        },
    },
});

cartLineCardCondensedStyles.quantity = mergeToNew(cartLineCardCondensedStyles.quantity, {
    editableQuantityTextField: {
        cssOverrides: {
            formInputWrapper: css`
                max-width: 100px;
                margin-top: 0;
            `,
            formField: css`
                [class*="FormFieldLabel"] {
                    text-transform: capitalize;
                    font-size: ${fontSizes.small};
                    line-height: ${lineHeights.default};
                    color: ${themeBackgrounds.darkGray};
                }
            `,
        },
    },
});

cartLineCardCondensedStyles.quantityGridItem = mergeToNew(cartLineCardCondensedStyles.quantityGridItem, {
    width: 12,
    css: css`
        [data-test-selector="quoteDetail_cardLine_qty"] {
            [data-test-selector="heading"] {
                font-size: ${fontSizes.small};
                line-height: ${lineHeights.default};
                color: ${themeBackgrounds.darkGray};
                text-transform: capitalize;
                margin-bottom: 10px;
            }
            [data-test-selector="text_Qty"] {
                font-size: ${fontSizes.small};
                line-height: ${lineHeights.default};
            }
        }
    `,
});

cartLineCardCondensedStyles.cartExtendedPrice = mergeToNew(cartLineCardCondensedStyles.cartExtendedPrice, {
    heading: {
        as: "span",
        size: fontSizes.small,
        lineHeight: lineHeights.default,
        color: themeBackgrounds.darkGray,
        transform: "capitalize",
        css: css`
            margin-bottom: 10px;
        `,
    },
    text: {
        size: fontSizes.small,
        lineHeight: lineHeights.default,
    },
});

cartLineCardCondensedStyles.requestedShipDateTextField = mergeToNew(
    cartLineCardCondensedStyles.requestedShipDateTextField,
    {
        cssOverrides: {
            formInputWrapper: css`
                max-width: 100px;
                input:not(.react-datetime-picker__inputGroup__input) {
                    padding: 0 4px;
                }
            `,
            formField: css`
                [class*="FormFieldLabel"] {
                    text-transform: capitalize;
                    font-size: ${fontSizes.small};
                    line-height: ${lineHeights.default};
                    color: ${themeBackgrounds.darkGray};
                }

                input:not(.react-datetime-picker__inputGroup__input) {
                    padding: 0 4px;
                    font-size: ${fontSizes.xSmall};
                    line-height: ${lineHeights.default};

                    &:focus {
                        padding: 0 4px;
                        border-width: 1px;
                    }
                }
            `,
        },
    },
);

cartLineCardCondensedStyles.productImage = mergeToNew(cartLineCardCondensedStyles.productImage, {
    linkWrappingImage: {
        css: css`
            width: auto;
        `,
    },
    img: {
        css: css`
            display: block;
            max-width: 40px;
        `,
    },
});

cartLineCardCondensedStyles.cartLineInfoGridItem = mergeToNew(cartLineCardCondensedStyles.cartLineInfoGridItem, {
    css: css`
        
        ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(
                theme,
                [
                    null,
                    null,
                    null,
                    css`
                        > div,
                        > div > div,
                        > div > div > div,
                        > div > div > div > div {
                        height: 100%;
                        display: flex;
                        }

                        > div > div {
                            align-self: center; 
                        }
                    `,
                ],
                "min",
            )}
    `
});

cartLineCardCondensedStyles.productPriceAndQuantityContainer = mergeToNew(
    cartLineCardCondensedStyles.productPriceAndQuantityContainer,
    {
        gap: 0,
        css: css`
            > ${GridOffset} {
                display: grid;
                grid-auto-flow: column;
                grid-auto-columns: minmax(auto, 1fr);
                gap: 8px;

                > div ,
                > div > section {
                    justify-content: center;
                    flex-direction: column;
                    display: flex;
                }

                @media only screen and (max-width: 992px) {
                    grid-auto-flow: row;
                    grid-template-columns: 1fr 1fr;
                    grid-template-rows: 1fr 1fr;
                }
            }
        `,
    },
);

cartLineCardCondensedStyles.productPriceAndAvailabilityGridItem = mergeToNew(
    cartLineCardCondensedStyles.productPriceAndAvailabilityGridItem,
    {
        width: 12,
    },
);

cartLineCardCondensedStyles.productPriceAndQuantityGridItem = mergeToNew(
    cartLineCardCondensedStyles.productPriceAndQuantityGridItem,
    {
        width: 12,
    },
);

cartLineCardCondensedStyles.productImageGridItem = mergeToNew(cartLineCardCondensedStyles.productImageGridItem, {
    css: css`
        display: flex;
        align-items: flex-start;
        gap: 8px;
        ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(
                theme,
                [
                    null,
                    null,
                    null,
                    css`
                        align-items: center;
                    `,
                ],
                "min",
            )}
    `,
});

cartLineCardCondensedStyles.productErpNumberText = mergeToNew(cartLineCardCondensedStyles.productErpNumberText, {
    size: fontSizes.small,
    lineHeight: lineHeights.default,
    weight: fontWeights.semiBold,
    css: css`
        display: block;
    `,
});

cartLineCardCondensedStyles.productDetailLink = mergeToNew(cartLineCardCondensedStyles.productDetailLink, {
    hoverStyle: {
        textDecoration: "none",
    },
    css: css`
        display: block;
    `,
    typographyProps: {
        underline: false,
        css: css`
            display: block;
            margin-bottom: 4px;
            text-decoration: none;
        `,
    },
});

cartLineCardCondensedStyles.countryOfOrigin = mergeToNew(cartLineCardCondensedStyles.countryOfOrigin, {
    as: "p",
    size: fontSizes.small,
    lineHeight: lineHeights.small,
});

cartLineCardCondensedStyles.badgeRed = mergeToNew(cartLineCardCondensedStyles.badgeRed, {
    as: "p",
    size: fontSizes.xSmall,
    lineHeight: lineHeights.xSmall,
    color: themeColors.danger,
    css: css`
        border: 1px solid ${themeColors.danger};
        width: fit-content;
        padding: 2px 5px;
        margin-top: 4px;
    `,
});

cartLineCardCondensedStyles.productDescription = mergeToNew(cartLineCardCondensedStyles.productDescription, {
    productDetailLink: {
        typographyProps: {
            size: fontSizes.small,
            lineHeight: lineHeights.default,
        },
    },
    productDetailDescription: {
        size: fontSizes.small,
        lineHeight: lineHeights.default,
    },
});

cartLineCardCondensedStyles.productAvailability = mergeToNew(cartLineCardCondensedStyles.productAvailability, {
    messageText: {
        size: fontSizes.xSmall,
        lineHeight: lineHeights.default,
    },
    errorWrapper: {
        css: css`
            margin-top: 4px;
        `,
    },
    errorText: {
        size: fontSizes.small,
        lineHeight: lineHeights.small,
        color: themeColors.danger,
        weight: fontWeights.regular,
    },
    messageGridItem: {
        css: css`
            [class*="InventoryMessage"] {
                margin-top: 4px;
                border-radius: 0;
                ${IconWrapper} {
                    width: 14px;
                    height: 14px;
                    margin-right: 4px;

                    svg {
                        width: 14px;
                        height: 14px;
                    }
                }
            }
        `,
    },
    availabilityByWarehouseLink: {
        typographyProps: {
            size: fontSizes.xSmall,
            lineHeight: lineHeights.xSmall,
        },
        css: css`
            margin-top: 4px;
        `,
    },
});

cartLineCardCondensedStyles.estShipDate = mergeToNew(cartLineCardCondensedStyles.estShipDate, {
    css: css`
        ul {
            padding-left: 16px;
            list-style: decimal;
            margin: 4px 0;

            li {
                font-size: ${fontSizes.small};
                line-height: ${lineHeights.default};
            }
        }
    `,
});

cartLineCardCondensedStyles.shipDateModal = mergeToNew(cartLineCardCondensedStyles.shipDateModal, {
    css: css`
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
    `,
});

cartLineCardCondensedStyles.shipToLocationGridItem = mergeToNew(cartLineCardCondensedStyles.shipToLocationGridItem, {
    css: css`
        flex-direction: column;
        ${({ theme }: { theme: BaseTheme }) =>
        breakpointMediaQueries(
            theme,
            [
                null,
                null,
                null,
                css`
                    .distribution-center {
                        margin-top: -8px;
                    }
                `,
            ],
            "min",
        )}
    `,
});

cartLineCardCondensedStyles.shipToLocationCenter = mergeToNew(cartLineCardCondensedStyles.shipToLocationCenter, {
    size: fontSizes.xSmall,
    lineHeight: lineHeights.xSmall,
    css: css`
        display: block;
    `,
});

cartLineCardCondensedStyles.shipToLocationName = mergeToNew(cartLineCardCondensedStyles.shipToLocationName, {
    size: fontSizes.xSmall,
    lineHeight: lineHeights.xSmall,
    color: themeBackgrounds.darkGray,
    css: css`
        display: block;
    `,
});

cartLineCardCondensedStyles.shipToLocationLink = mergeToNew(cartLineCardCondensedStyles.shipToLocationLink, {
    typographyProps: {
        size: fontSizes.xSmall,
        lineHeight: lineHeights.xSmall
    },
});

cartLineCardCondensedStyles.selecteProductModalTitle = mergeToNew(
    cartLineCardCondensedStyles.selecteProductModalTitle,
    {
        css: css`
            display: flex;
            flex-direction: row;
            gap: 4px;
        `,
    },
);

cartLineCardCondensedStyles.selectedProductErpNo = mergeToNew(cartLineCardCondensedStyles.selectedProductErpNo, {
    variant: "h4",
    weight: fontWeights?.bold,
    css: css`
        margin-bottom: 0;
    `,
});

cartLineCardCondensedStyles.selecteProductTitle = mergeToNew(cartLineCardCondensedStyles.selecteProductTitle, {
    variant: "h4",
    weight: fontWeights?.regular,
    css: css`
        margin-bottom: 0;
    `,
});

cartLineCardCondensedStyles.requestedDatePickerGridItem = mergeToNew(
    cartLineCardCondensedStyles.requestedDatePickerGridItem,
    {
        css: css`
            min-width: 96px;
			> div {
                display: flex;
            }
        `,
    },
);

cartLineCardCondensedStyles.requestedDatePicker = mergeToNew(cartLineCardCondensedStyles.requestedDatePicker, {
    cssOverrides: {
        datePicker: css`
            .react-calendar__year-view__months__month,
            .react-calendar__decade-view__years__year {
                &:disabled {
                    color: ${themeBackgrounds.lightGray};
                    cursor: not-allowed;
                }
            }

            .react-datetime-picker {
                &:not(:disabled),
                &:focus-within {
                    padding: 0;
                    .react-datetime-picker__inputGroup {
                        padding: 5px;
                    }
                }
            }
        `,
        formField: css`
            [class*="FormFieldLabel"] {
                font-size: ${fontSizes.small};
                line-height: ${lineHeights.default};
                color: ${themeBackgrounds.darkGray};
                text-transform: capitalize;
            }

            .react-datetime-picker {
                font-size: ${fontSizes.small};
                line-height: ${lineHeights.default};
            }
        `,
    },
});
